import React, { useEffect, useState } from 'react';
import Button from '../../../../../../components/controls/Button';
import BaseModal from '../../../../../../components/modals/BaseModal';
import { Alert } from '../../../../../../components/visual/Alert/Alert';
import { apiPUT } from '../../../../../../utils/networking';
import useMessages from '../../../../../../utils/useMessages';

/**
 *
 * @param {{
 *  delegate: import('../../../../../../store/reducers/delegatesReducer').TDelegateListItem;
 *  onSuccess?():void;
 * }} props
 * @returns {JSX.Element} - The rendered DelegateModal component.
 */
export const DelegateModalAlterStateInvoker = (props) => {
  const { delegate, onSuccess } = props;
  const [isOpened, setIsOpened] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const messages = useMessages();

  const submit = async () => {
    setIsBusy(true);
    setErrorMessage('');
    try {
      const payload = {
        ...delegate,
        enabled: !delegate.enabled
      }
      const resp = await apiPUT(`/recommendation/rest/evaluatorDelegate`, payload);
      if (/^20/.test(resp.status)) {
        onSuccess?.();
        onModalClose();
        payload.enabled
          ? messages.clear().notifyGreen(`You have activated ${delegate.firstName} ${delegate.lastName} as a delegate. They can now help manage your evaluations.`)
          : messages.clear().notifyYellow(`You have deactivated ${delegate.firstName} ${delegate.lastName} as a delegate. They can no longer help manage your evaluations. If you want ${delegate.firstName} ${delegate.lastName} to help manage your evaluations, you must reactivate them.`);
      } else {
        throw new Error('An error occurred while deleting delegate. Please try again.');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(
        'An error occurred while deleting delegate. Please try again.'
      );
    } finally {
      setIsBusy(false);
    }
  }

  const modalBody = () => {
    return <>
      <Alert show={!!errorMessage} message={errorMessage} type={'red'} className="mb-2" />
      <p tabIndex={0} className="mb-2">
        {
          delegate.enabled
            ? `Are you sure you want to deactivate ${delegate.firstName} ${delegate.lastName} as a delegate? They will no longer be able to help manage your evaluations.`
            : `Are you sure you want to reactivate ${delegate.firstName} ${delegate.lastName} as a delegate? They will be able to help manage your evaluations.`
        }
      </p>
    </>;
  }

  const modalControls = () => {
    return <>
      <Button
        onClick={onModalClose}
        outline
        primary
        disabled={isBusy}
      >
        Cancel
      </Button>
      <Button
        onClick={submit}
        danger={delegate.enabled}
        primary={!delegate.enabled}
        disabled={isBusy}
        isBusy={isBusy}
      >
        {delegate.enabled ? 'Deactivate' : 'Reactivate'}
      </Button>
    </>
  }

  const onModalClose = () => {
    setIsOpened(false);
    setErrorMessage('');
  }

  useEffect(() => {
    return () => {
      onModalClose();
    }
  }, []);

  return <>
    <Button
      link
      danger={delegate.enabled}
      primary={!delegate.enabled}
      onClick={() => {
        setIsOpened(true);
      }}>
      {delegate.enabled ? 'Deactivate' : 'Reactivate'}
    </Button>
    <BaseModal
      size='compact'
      initialFocus={false}
      show={isOpened}
      isBusy={isBusy}
      title={delegate.enabled ? 'Confirm Deactivation' : 'Confirm Reactivation'}
      modalBodyClass={'text-center'}
      closeIcon={true}
      body={modalBody()}
      controls={modalControls()}
      onClose={() => {
        onModalClose?.();
      }}
    />
  </>
}
